import Vue from 'vue';
import { mapActions } from 'vuex';
import { db, FieldValue } from '@/firebase';
import {
	ResultFavorite,
	RunnitNodeRun,
	RunnitNodeRunResult,
	RUNNIT_NODE_RUN_RESULT_TYPE,
} from '@run-diffusion/shared';
import { SNACKBAR_STATUS } from '@/constants/constants';
import { analytics, EVENTS } from '@/services/analytics';

type FavoritesListener = (favorites: ResultFavorite[]) => void;

const FAVORITABLE_RESULT_TYPES: string[] = [
	RUNNIT_NODE_RUN_RESULT_TYPE.IMG,
	RUNNIT_NODE_RUN_RESULT_TYPE.VID,
	RUNNIT_NODE_RUN_RESULT_TYPE.LAYERS,
	RUNNIT_NODE_RUN_RESULT_TYPE.ASSET_3D,
];

let unsubscribe: (() => void) | null = null;
let boundUserId: string | null = null;
let cachedFavorites: ResultFavorite[] = [];
let cachedFavoriteIdsKey: string = '';
const listeners = new Set<FavoritesListener>();
const listenerInstances = new WeakMap<Vue, FavoritesListener>();
const inFlightToggles = new Set<string>();

const notifyAll = () => {
	listeners.forEach((fn) => fn(cachedFavorites));
};

const startBindIfNeeded = (userId: string) => {
	if (boundUserId === userId && unsubscribe) return;
	stopBind();
	boundUserId = userId;
	// index created: resultFavorites - userId Ascending createdAt Descending __name__ Descending
	unsubscribe = db.collection('resultFavorites')
		.where('userId', '==', userId)
		.orderBy('createdAt', 'desc')
		.onSnapshot((snap) => {
			const next: ResultFavorite[] = snap.docs.map((d) => ({ id: d.id, ...d.data() }) as ResultFavorite);
			// Suppress notifications when the favorited-result set is unchanged (e.g. serverTimestamp echo).
			const nextKey: string = next.map((f) => f.id).join('|');
			if (nextKey === cachedFavoriteIdsKey) return;
			cachedFavorites = next;
			cachedFavoriteIdsKey = nextKey;
			notifyAll();
		}, (err) => {
			console.error('resultFavorites listener error', err);
		});
};

const stopBind = () => {
	if (unsubscribe) unsubscribe();
	unsubscribe = null;
	boundUserId = null;
	inFlightToggles.clear();
	if (cachedFavorites.length === 0) return;
	cachedFavorites = [];
	cachedFavoriteIdsKey = '';
	notifyAll();
};

export const ResultFavoritesMixin = Vue.extend({
	data () {
		return {
			localResultFavorites: [] as ResultFavorite[],
		};
	},
	computed: {
		favoritesByResultUuid (): Map<string, ResultFavorite> {
			const map = new Map<string, ResultFavorite>();
			for (const fav of this.localResultFavorites) {
				if (fav?.nodeRunResultUuid) map.set(fav.nodeRunResultUuid, fav);
			}
			return map;
		},
	},
	created () {
		const fn: FavoritesListener = (favs) => {
			this.localResultFavorites = favs;
		};
		listeners.add(fn);
		listenerInstances.set(this, fn);
		this.localResultFavorites = cachedFavorites;
	},
	beforeDestroy () {
		const fn = listenerInstances.get(this);
		if (fn) {
			listeners.delete(fn);
			listenerInstances.delete(this);
		}
		if (listeners.size === 0) stopBind();
	},
	watch: {
		'$store.state.user.id': {
			immediate: true,
			handler (newId: string | null) {
				if (newId) startBindIfNeeded(newId);
				else stopBind();
			},
		},
	},
	methods: {
		...mapActions(['updateSnackbar']),
		buildResultFavoriteId (userId: string, nodeRunResultUuid: string): string {
			return `${userId}_${nodeRunResultUuid}`;
		},
		isResultFavorited (nodeRunResult: RunnitNodeRunResult | null | undefined): boolean {
			if (!nodeRunResult?.uuid) return false;
			return this.favoritesByResultUuid.has(nodeRunResult.uuid);
		},
		canFavoriteResult (nodeRun: RunnitNodeRun | null | undefined, nodeRunResult: RunnitNodeRunResult | null | undefined): boolean {
			const userId: string | null = this.$store.state.user?.id || null;
			if (!userId || !nodeRun || !nodeRunResult) return false;
			if (nodeRun.userId !== userId) return false;
			if (nodeRun.state !== 'DONE') return false;
			return FAVORITABLE_RESULT_TYPES.includes(nodeRunResult.type);
		},
		async toggleResultFavorite (nodeRun: RunnitNodeRun, nodeRunResult: RunnitNodeRunResult): Promise<void> {
			if (!this.canFavoriteResult(nodeRun, nodeRunResult)) return;
			const userId: string = this.$store.state.user.id;
			const uuid = nodeRunResult.uuid;
			const docId = this.buildResultFavoriteId(userId, uuid);
			if (inFlightToggles.has(docId)) return;
			inFlightToggles.add(docId);

			const wasFavorited = this.isResultFavorited(nodeRunResult);
			try {
				if (wasFavorited) {
					await db.doc(`resultFavorites/${docId}`).delete();
				} else {
					// Coerce absent ids to null, do NOT omit them. This app does not
					// enable ignoreUndefinedProperties, so an undefined value makes
					// Firestore reject the whole write ("Could not favorite"). Omitting
					// the key instead is also wrong: the resultFavorites create rule
					// compares each id against the source run
					// (`src.nodeId == data.nodeId`, `src.runnitId == data.runnitId`),
					// so a missing key is rejected by rules for any run that HAS a
					// nodeId. null is the only value that satisfies both, and it
					// matches a source run whose own field is null.
					await db.doc(`resultFavorites/${docId}`).set({
						createdAt: FieldValue.serverTimestamp(),
						userId,
						nodeRunId: nodeRun.id,
						nodeRunResultUuid: uuid,
						nodeDefId: nodeRun.nodeDefId ?? null,
						nodeId: nodeRun.nodeId ?? null,
						runnitId: nodeRun.runnitId ?? null,
						resultType: nodeRunResult.type,
					});
				}
				// High-intent engagement signal: only fire on the favoriting action,
				// not on unfavorite (unfavorite is a correction, not a positive signal).
				if (!wasFavorited) {
					analytics.track(EVENTS.RESULT_FAVORITED, {
						result_type: nodeRunResult.type,
						node_run_id: nodeRun.id,
						node_def_id: nodeRun.nodeDefId,
						runnit_id: nodeRun.runnitId,
					});
				}
			} catch (err: any) {
				console.error('toggleResultFavorite failed', err);
				this.updateSnackbar({
					status: SNACKBAR_STATUS.ERROR,
					message: wasFavorited ? 'Could not unfavorite — please try again' : 'Could not favorite — please try again',
					show: true,
				});
			} finally {
				inFlightToggles.delete(docId);
			}
		},
	},
});
